// Remote logger for diagnostic purposes

let LOG_URL;
if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
    LOG_URL = "http://localhost:8001/log";
} else {
    LOG_URL = window.location.origin + "/1/log";
}

let isSending = false;
let logCount = 0;
const MAX_LOGS = 50; // Session rate limit

export function initRemoteLogger() {
    if (typeof window === "undefined") return;

    // Get or create a persistent device identifier
    let deviceId = localStorage.getItem("transfer_zip_device_id");
    if (!deviceId) {
        deviceId = "device_" + Math.random().toString(36).substring(2, 10) + "_" + Date.now().toString(36);
        localStorage.setItem("transfer_zip_device_id", deviceId);
    }

    const sendLog = async (level, message, stack = "") => {
        if (isSending || logCount >= MAX_LOGS) return;
        
        // Skip log payloads that look like our own log requests failing, to avoid infinite loops
        if (message && (message.includes("/log") || message.includes("Failed to fetch") || message.includes("NetworkError"))) {
            return;
        }

        isSending = true;
        logCount++;

        try {
            await fetch(LOG_URL, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    deviceId,
                    level,
                    message,
                    stack,
                    url: window.location.href,
                    userAgent: navigator.userAgent,
                    timestamp: new Date().toISOString()
                })
            });
        } catch (e) {
            // Silently swallow logging errors to prevent loops
        } finally {
            isSending = false;
        }
    };

    // Hook console.error
    const originalConsoleError = console.error;
    console.error = function (...args) {
        originalConsoleError.apply(console, args);
        
        const message = args.map(arg => {
            if (arg instanceof Error) return arg.message;
            if (typeof arg === "object") {
                try { return JSON.stringify(arg); } catch (e) { return String(arg); }
            }
            return String(arg);
        }).join(" ");
        
        let stack = "";
        const errorArg = args.find(arg => arg instanceof Error);
        if (errorArg) {
            stack = errorArg.stack || "";
        } else {
            // Capture current stack trace
            try {
                throw new Error();
            } catch (e) {
                stack = e.stack || "";
                // Remove first couple of lines from the captured trace pointing to logger.js
                const lines = stack.split("\n");
                if (lines.length > 2) {
                    stack = lines.slice(2).join("\n");
                }
            }
        }
        
        sendLog("ERROR", message, stack);
    };

    // Hook console.warn
    const originalConsoleWarn = console.warn;
    console.warn = function (...args) {
        originalConsoleWarn.apply(console, args);
        
        const message = args.map(arg => {
            if (typeof arg === "object") {
                try { return JSON.stringify(arg); } catch (e) { return String(arg); }
            }
            return String(arg);
        }).join(" ");
        
        sendLog("WARN", message);
    };

    // Listen to global unhandled exceptions
    window.addEventListener("error", (event) => {
        const message = event.message || "Unhandled exception";
        const stack = event.error ? event.error.stack : "";
        sendLog("FATAL", message, stack);
    });

    // Listen to unhandled promise rejections
    window.addEventListener("unhandledrejection", (event) => {
        const reason = event.reason;
        const message = reason ? (reason.message || String(reason)) : "Unhandled promise rejection";
        const stack = reason && reason.stack ? reason.stack : "";
        sendLog("FATAL", message, stack);
    });

    console.log("[RemoteLogger] Initialized. Device ID:", deviceId, "Endpoint:", LOG_URL);
}
